import { updateShopifyCartAttributes, updateShopifyCartLines } from '@portal/api/shopifyV2/cart';
import { useSessionValue } from '@portal/appLayer/auth';
import { StoreCode } from '@portal/schema/storeCode';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { useShopifyCartUrl } from '@portal/shared/hooks/useShopifyCartUrl';
import { CartRules } from '@portal/shared/utils/cartRules';
import { safeLocalStorage } from '@portal/shared/utils/safeLocalStorage';
import { getIdFromShopifyFormattedId } from '@portal/shared/utils/shopify/ids';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useStoreCountryCode } from '@portal/state/hooks/useStoreCountryCode';
import { useShopV3CartStore } from '@portal/state/store/cartStore';
import { CartDetails, CartLine, CartLineItem, CartUpdateInput } from 'api/shopifyV2/types';
import { useRouter } from 'next/router';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cookies from 'js-cookie';
import { formatCartBuyerIdentityInput } from '../../CartModal/utils/getBuyerIndentityInput';
import { useCustomerDetails } from './useCustomerDetails';

interface SiteCart {
  cart: CartDetails | null;
  cartId: string;
  cartCount: number;
  otpItems: CartLine[];
  subItems: CartLine[];
  freeItems: CartLine[];
  paidOtpItems: CartLine[];
  paidSubItems: CartLine[];
  isOpen: boolean;
  isCartLoading: boolean;
  isCartUpdating: boolean;
  cartError: Error | null;
  quantityErrors: Array<{
    merchandiseId: string;
    requestedQuantity: number;
    cappedQuantity: number;
    errorMessage: string;
  }>;
  openCart: () => void;
  closeCart: () => void;
  setCartOpen: (isOpen: boolean) => void;
  refetchCartData: (newCartId?: string) => Promise<CartDetails | null>;
  addLinesToCart(
    linesToAdd: CartLineItem[],
    attributes?: { key: string; value: string }[]
  ): Promise<CartDetails | null>;
  updateCartItemQuantity: (
    cartLineItem: CartLine,
    newQuantity: number
  ) => Promise<CartDetails | null>;
  subscribeToCartItem: (
    cartLineItem: CartLine,
    subscriptionMerchandiseId: string,
    sellingPlanId: string
  ) => Promise<CartDetails | null>;
  switchSellingPlan: (cartLineItemsToSwitch: CartLine[], sellingPlanId: string) => Promise<void>;
  clearCart: () => void;
  deleteCart: () => void;
  submitCart: () => void;
  clearQuantityErrors: () => void;
  isDebugMode: boolean;
  setIsDebugMode: (val: boolean) => void;
  debugHighlightCartLines: string[];
  checkDebugHighlightProductIds: (idToCheck: string, qty: number) => Promise<void>;
}

const SiteCartContext = createContext<SiteCart | undefined>(undefined);

export const useSiteCart = (): SiteCart => {
  const context = React.useContext(SiteCartContext);
  if (context === undefined) {
    throw new Error('useSiteCart must be used within a SiteCartProvider');
  }
  return context;
};

interface SiteCartProviderProps {
  children: React.ReactNode;
}

export const SiteCartProvider = ({ children }: SiteCartProviderProps) => {
  const router = useRouter();
  const locale = useLocale();
  const store = useStoreValue();
  const session = useSessionValue();
  const countryCode = useStoreCountryCode();
  const { getShopifyCartUrl } = useShopifyCartUrl();
  const [isDebugMode, setIsDebugMode] = useState(false);

  const cart = useShopV3CartStore((state) => state.data.cart);
  const isOpen = useShopV3CartStore((state) => state.data.isOpen);
  const isCartLoading = useShopV3CartStore((state) => state.loading.isCartLoading);
  const isCartUpdating = useShopV3CartStore((state) => state.loading.isCartUpdating);
  const cartError = useShopV3CartStore((state) => state.errors.cartError);
  const quantityErrors = useShopV3CartStore((state) => state.errors.quantityErrors);
  const fetchCartData = useShopV3CartStore((state) => state.actions.fetchCartData);
  const addToCart = useShopV3CartStore((state) => state.actions.addToCart);
  const updateCart = useShopV3CartStore((state) => state.actions.updateCart);
  const clearQuantityErrors = useShopV3CartStore((state) => state.actions.clearQuantityErrors);

  const updateCartBuyerIdentity = useShopV3CartStore(
    (state) => state.actions.updateCartBuyerIdentity
  );
  const clearCart = useShopV3CartStore((state) => state.actions.clearCart);
  const deleteCart = useShopV3CartStore((state) => state.actions.deleteCart);
  const submitCart = useShopV3CartStore((state) => state.actions.submitCart);
  const openCart = useShopV3CartStore((state) => state.actions.openCart);
  const closeCart = useShopV3CartStore((state) => state.actions.closeCart);
  const setCartOpen = useShopV3CartStore((state) => state.actions.setCartOpen);

  const stableStoreCode = useMemo(() => store, [store]);
  const stableCartId = useMemo(
    () => cart?.id || safeLocalStorage.getItem('cartId') || '',
    [cart?.id]
  );

  // Debug
  const [debugHighlightCartLines, setDebugHighlightCartLines] = useState<string[]>([]);

  const customerAccessToken = useMemo(
    () => session?.accessTokenPayload.shopify?.accessToken,
    [session?.accessTokenPayload.shopify?.accessToken]
  );
  const { customer } = useCustomerDetails(store, customerAccessToken);

  // Track original order of cart lines to prevent reordering on quantity changes
  // Use stable identifiers based on merchandise and selling plan, not cart line IDs
  const stableOrderRef = useRef<Map<string, number>>(new Map());

  const [otpItems, subItems, freeItems, paidOtpItems, paidSubItems] = useMemo(() => {
    if (!cart) return [[], [], [], [], []];

    // Create stable identifier for each cart line
    const getStableId = (line: CartLine) => {
      const merchandiseId = line.merchandise.id;
      const sellingPlanId = line.sellingPlanAllocation?.sellingPlan.id || 'no-plan';
      // Include flavor attribute if it exists to distinguish between different flavors
      const flavorAttr = line.attributes?.find((attr) => attr.key === '__flavor')?.value || '';
      return `${merchandiseId}-${sellingPlanId}-${flavorAttr}`;
    };

    // Update stable order tracking for new items
    cart.lines.forEach((line) => {
      const stableId = getStableId(line);
      if (!stableOrderRef.current.has(stableId)) {
        stableOrderRef.current.set(stableId, stableOrderRef.current.size);
      }
    });

    // Sort cart lines by their stable order
    const sortedLines = [...cart.lines].sort((a, b) => {
      const orderA = stableOrderRef.current.get(getStableId(a)) ?? 0;
      const orderB = stableOrderRef.current.get(getStableId(b)) ?? 0;
      return orderA - orderB;
    });

    // All OTP items
    const otpItemsValue = [
      ...sortedLines.filter(
        (line) =>
          !line.sellingPlanAllocation?.sellingPlan ||
          !line.attributes?.find((attribute) => attribute.key.includes('shop_subscriptions'))
      )
    ];
    // All sub items
    const subItemsValue = [
      ...sortedLines.filter(
        (line) =>
          !!line.sellingPlanAllocation?.sellingPlan ||
          !!line.attributes?.find((attribute) => attribute.key.includes('shop_subscriptions'))
      )
    ];
    // All free items
    const freeLinesValue = [
      ...sortedLines.filter((line) => parseFloat(line.cost.totalAmount.amount || '0') === 0)
    ];
    // All paid OTP items
    const paidOtpItemsValue = [
      ...otpItemsValue.filter((line) => !freeLinesValue.some((freeLine) => freeLine.id === line.id))
    ];
    // All paid sub items
    const paidSubItemsValue = [
      ...subItemsValue.filter((line) => !freeLinesValue.some((freeLine) => freeLine.id === line.id))
    ];

    return [otpItemsValue, subItemsValue, freeLinesValue, paidOtpItemsValue, paidSubItemsValue];
  }, [cart]);

  const cartCount = useMemo(() => {
    return cart?.lines?.reduce((acc, line) => acc + line.quantity, 0) || 0;
  }, [cart]);

  const refetchCartData = useCallback(
    async (newCartId?: string) => {
      const cartId = newCartId || stableCartId;
      if (!cartId) return null;
      const data = await fetchCartData(stableStoreCode, cartId);
      return data;
    },
    [fetchCartData, stableStoreCode, stableCartId]
  );

  // Handles adding items to cart (both single and multiple items)
  const addLinesToCart = useCallback(
    async (lines: CartLineItem[], attributes?: { key: string; value: string }[]) => {
      try {
        const updatedCart = await addToCart(stableStoreCode, {
          cartId: stableCartId,
          lines,
          ...(attributes && { attributes })
        });
        // Return the fresh cart data from the API response
        return updatedCart;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error adding items to cart:', error);
        throw error;
      }
    },
    [addToCart, stableStoreCode, stableCartId]
  );

  // Handles updating cart item quantities (including removal when quantity is 0)
  const updateCartItemQuantity = useCallback(
    async (cartLineItem: CartLine, newQuantity: number) => {
      if (!cart) return null;

      try {
        // Check if this update would leave only free items (before making any API calls)
        const wouldLeaveOnlyFreeItems =
          newQuantity === 0 &&
          cart.lines.length > 1 &&
          cart.lines
            .filter((line) => line.id !== cartLineItem.id) // Exclude the item being removed
            .every((line) => parseFloat(line.cost.totalAmount.amount || '0') === 0);

        // If removing this item would leave only free items, clear the entire cart in one operation
        if (wouldLeaveOnlyFreeItems) {
          const clearedCart = await clearCart(stableStoreCode);
          return clearedCart;
        }

        // Use line ID-based update for simple quantity changes
        // This is much more efficient as it uses updateShopifyCartLines directly
        // instead of the remove-all + add-all approach used by merchandise ID updates
        const cartUpdateInput: CartUpdateInput = {
          cartId: stableCartId,
          lines: [
            {
              id: cartLineItem.id,
              quantity: newQuantity
            }
          ]
        };

        // Call the updateCart with the line ID input
        const updatedCart = await updateCart(stableStoreCode, cartUpdateInput);

        return updatedCart;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating cart quantity:', error);
        throw error;
      }
    },
    [cart, stableCartId, stableStoreCode, updateCart, clearCart]
  );

  const switchSellingPlan = useCallback(
    async (cartLineItemsToSwitch: CartLine[], sellingPlanId: string) => {
      if (!stableCartId) {
        throw new Error('No cart ID available');
      }

      try {
        const cartUpdateInput: CartUpdateInput = {
          cartId: stableCartId,
          lines: cartLineItemsToSwitch.map((line) => ({
            id: line.id,
            sellingPlanId
          }))
        };

        // Use updateShopifyCartLines directly for simple selling plan updates
        // This avoids the complex remove-all+add-all logic in updateCart
        const updatedCart = await updateShopifyCartLines(stableStoreCode, cartUpdateInput);

        // Update the cart state directly with the response
        // We need to manually update the store state since we bypassed the cart store's updateCart
        const currentState = useShopV3CartStore.getState();
        useShopV3CartStore.setState({
          ...currentState,
          data: {
            ...currentState.data,
            cart: updatedCart
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error switching selling plan:', error);
        throw error;
      }
    },
    [stableCartId, stableStoreCode]
  );

  // Convert a one-time purchase item to subscription atomically
  const subscribeToCartItem = useCallback(
    async (cartLineItem: CartLine, subscriptionMerchandiseId: string, sellingPlanId: string) => {
      if (!cart) return null;

      try {
        // Use single cartLinesUpdate to atomically convert existing cart line to subscription
        // This is more efficient than remove + add operations
        const cartUpdateInput: CartUpdateInput = {
          cartId: stableCartId,
          lines: [
            {
              id: cartLineItem.id,
              merchandiseId: subscriptionMerchandiseId,
              quantity: cartLineItem.quantity,
              sellingPlanId,
              attributes: [
                {
                  key: '__shop_subscriptions',
                  value: 'true'
                }
              ]
            }
          ]
        };

        const updatedCart = await updateCart(stableStoreCode, cartUpdateInput);
        return updatedCart;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error converting to subscription:', error);
        throw error;
      }
    },
    [cart, stableCartId, stableStoreCode, updateCart]
  );

  const clearCartCallback = useCallback(async () => {
    await clearCart(stableStoreCode);
  }, [clearCart, stableStoreCode]);

  const deleteCartCallback = useCallback(async () => {
    await deleteCart();
  }, [deleteCart]);

  const submitCartHandler = useCallback(async () => {
    // Submit function is passed as a callback to pass through loading state management
    await submitCart(async () => {
      if (!cart) return;

      const currentUrl = window.location.href;
      const { id, checkoutUrl } = cart;

      const formattedReturnURl = `${currentUrl}&cartId=${getIdFromShopifyFormattedId(id)}`;
      const attributesInput = {
        cartId: id,
        attributes: [
          {
            key: 'checkoutOriginUrl',
            value: formattedReturnURl
          }
        ]
      };

      if (session?.customer?.tags?.length) {
        attributesInput.attributes.push({
          key: 'customerTags',
          value: JSON.stringify(session?.customer?.tags)
        });
      }

      const socialLadderTrackCode = cookies.get('sl-track-purchase');
      if (socialLadderTrackCode) {
        attributesInput.attributes.push({
          key: 'social_ladder.tracking_cookie',
          value: socialLadderTrackCode
        });
      }
      (await updateShopifyCartAttributes(store, attributesInput)) as CartDetails;

      const cartUrl = await getShopifyCartUrl(stableStoreCode, stableCartId, locale);
      // Fall back to the original URL if something goes wrong
      window.location.href = cartUrl || checkoutUrl;
    });
  }, [
    cart,
    getShopifyCartUrl,
    locale,
    session?.customer?.tags,
    stableCartId,
    stableStoreCode,
    store,
    submitCart
  ]);

  const addBuyerIdentityToCart = useCallback(async () => {
    if (!customer || !cart || isCartUpdating) return;

    const buyerIdentityInput = formatCartBuyerIdentityInput(
      customer,
      cart.id,
      countryCode,
      customerAccessToken
    );

    if (!cart.buyerIdentity?.email) {
      await updateCartBuyerIdentity(store, buyerIdentityInput);
    }
  }, [
    cart,
    countryCode,
    customer,
    customerAccessToken,
    isCartUpdating,
    store,
    updateCartBuyerIdentity
  ]);

  useEffect(() => {
    // Only refetch if we have a cartId but no cart data
    if (stableCartId && !cart) {
      refetchCartData();
    }
  }, [refetchCartData, stableCartId, cart]);

  useEffect(() => {
    addBuyerIdentityToCart();
  }, [addBuyerIdentityToCart]);

  useEffect(() => {
    const handleRouteChange = () => {
      closeCart();
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [closeCart, router.events]);

  // Cart rules instance for debug functionality
  const cartRulesRef = useRef(new CartRules());

  const checkDebugHighlightProductIds = useCallback(
    async (idToCheck: string, qty: number) => {
      setDebugHighlightCartLines([]);

      if (!cart || !idToCheck || !store) return;

      try {
        // Use cart rules to find auto-add items (freebies)
        const autoAddIds = await cartRulesRef.current.getAutoAddItemsForDebug(
          store as StoreCode,
          idToCheck,
          qty
        );

        const relatedCartLines = cart.lines.filter((product) =>
          autoAddIds.includes(product.merchandise.id)
        );
        setDebugHighlightCartLines(relatedCartLines.map((line) => line.id));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error in checkDebugHighlightProductIds:', error);
        // Clear highlighting on error
        setDebugHighlightCartLines([]);
      }
    },
    [cart, store]
  );

  const value: SiteCart = useMemo(
    () => ({
      cart,
      cartId: stableCartId,
      cartCount,
      subItems,
      otpItems,
      freeItems,
      paidOtpItems,
      paidSubItems,
      isOpen,
      isCartLoading,
      isCartUpdating,
      cartError,
      quantityErrors,
      openCart,
      closeCart,
      setCartOpen,
      refetchCartData,
      addLinesToCart,
      updateCart,
      updateCartItemQuantity,
      switchSellingPlan,
      subscribeToCartItem,
      clearCart: clearCartCallback,
      deleteCart: deleteCartCallback,
      submitCart: submitCartHandler,
      clearQuantityErrors,
      isDebugMode,
      setIsDebugMode,
      debugHighlightCartLines,
      checkDebugHighlightProductIds
    }),
    [
      cart,
      stableCartId,
      cartCount,
      subItems,
      otpItems,
      freeItems,
      paidOtpItems,
      paidSubItems,
      isOpen,
      isCartLoading,
      isCartUpdating,
      cartError,
      quantityErrors,
      openCart,
      closeCart,
      setCartOpen,
      refetchCartData,
      addLinesToCart,
      updateCart,
      updateCartItemQuantity,
      switchSellingPlan,
      subscribeToCartItem,
      clearCartCallback,
      deleteCartCallback,
      submitCartHandler,
      clearQuantityErrors,
      isDebugMode,
      debugHighlightCartLines,
      checkDebugHighlightProductIds
    ]
  );

  return <SiteCartContext.Provider value={value}>{children}</SiteCartContext.Provider>;
};
